import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout, { Section, PageHero } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import { api } from "@/lib/api";
import { ArrowUpRight, Search } from "lucide-react";

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("en", { year: "numeric", month: "short", day: "numeric" });
    } catch { return ""; }
}

export default function Insights() {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [cats, setCats] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [focused, setFocused] = useState(false);
    const [active, setActive] = useState("All");
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/insights/categories").then((r) => setCats(r.data?.categories || []));
    }, []);

    useEffect(() => {
        const h = setTimeout(() => {
            const q = query.trim();
            api.get(`/analytics/suggestions${q ? `?prefix=${encodeURIComponent(q)}` : ""}`)
                .then((r) => setSuggestions(r.data?.suggestions || [])).catch(() => setSuggestions([]));
        }, 150);
        return () => clearTimeout(h);
    }, [query]);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (active !== "All") params.set("category", active);
        if (query.trim()) params.set("q", query.trim());
        const qs = params.toString();
        const url = "/insights" + (qs ? `?${qs}` : "");
        const h = setTimeout(() => {
            api.get(url).then((r) => {
                setItems(r.data || []);
                if (query.trim()) {
                    api.post("/analytics/search", {
                        query: query.trim(), category: active === "All" ? null : active,
                        result_count: (r.data || []).length, locale: (navigator.language || "").slice(0, 2),
                    }).catch(() => {});
                }
            }).finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(h);
    }, [active, query]);

    const [featured, ...rest] = items;

    return (
        <Layout title="Insights — Ventara" description="Essays, notes, and technical writing from the Ventara studio.">
            <PageHero i18nKey="insights"
                eyebrow="Insights"
                title={<>Notes from<br />the studio.</>}
                lead="Essays, dispatches, and technical writing. We publish quietly, on our own rhythm."
            />

            <Section className="pb-8" data-testid="insights-filters">
                <div className="border-t border-border pt-8">
                    <div className="flex items-center gap-3 border-b border-border pb-3 mb-6 relative">
                        <Search className="h-4 w-4 text-muted-foreground" />
                        <input
                            type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                            onFocus={() => setFocused(true)} onBlur={() => setTimeout(() => setFocused(false), 150)}
                            placeholder={t("pages.insights.searchPh")}
                            data-testid="insights-search"
                            className="flex-1 bg-transparent outline-none text-lg font-serif placeholder:text-muted-foreground/50"
                        />
                        {focused && suggestions.length > 0 && (
                            <div className="absolute left-7 right-0 top-full mt-2 bg-background border border-border rounded-2xl p-2 z-10 shadow-xl" data-testid="search-suggestions">
                                {suggestions.map((s) => (
                                    <button key={s.query} onMouseDown={(e) => { e.preventDefault(); setQuery(s.query); }}
                                        data-testid={`suggestion-${s.query.replace(/\s+/g,'-')}`}
                                        className="w-full text-left px-4 py-2 rounded-xl text-sm hover:bg-muted flex items-center justify-between">
                                        <span>{s.query}</span>
                                        <span className="label">{s.count}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {["All", ...cats].map((c) => (
                            <button
                                key={c} onClick={() => setActive(c)}
                                data-testid={`insight-filter-${c.toLowerCase().replace(/\s+/g,'-')}`}
                                className={`px-4 py-2 rounded-full text-xs border transition-colors ${
                                    active === c ? "bg-foreground text-background border-foreground"
                                                : "border-border hover:border-foreground/40 text-muted-foreground hover:text-foreground"}`}
                            >{c}</button>
                        ))}
                    </div>
                </div>
            </Section>

            <Section className="pb-24">
                {loading && <p className="prose-body">Loading…</p>}
                {!loading && !featured && <p className="prose-body">Nothing here yet — try another category.</p>}
                {featured && (
                    <Reveal>
                        <Link to={`/insights/${featured.slug}`} data-testid="insight-featured" className="group grid lg:grid-cols-12 gap-12 items-center border-t border-border pt-12">
                            <div className="lg:col-span-7 overflow-hidden rounded-3xl aspect-[16/10] bg-muted">
                                <img src={featured.cover_image} alt={featured.cover_image_alt || ""} loading="lazy"
                                     className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105" />
                            </div>
                            <div className="lg:col-span-5">
                                <p className="label">{featured.category} · {featured.read_minutes} min read</p>
                                <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 tracking-tight leading-[1.05]">{featured.title}</h2>
                                <p className="prose-body mt-6">{featured.excerpt}</p>
                                <p className="label mt-8">{formatDate(featured.published_at)}</p>
                                <span className="mt-8 inline-flex items-center gap-2 text-sm border-b border-foreground/40 pb-1 group-hover:border-foreground transition-colors">
                                    Read the essay <ArrowUpRight className="h-4 w-4" />
                                </span>
                            </div>
                        </Link>
                    </Reveal>
                )}
            </Section>

            <Section className="pb-32">
                <div className="border-t border-border pt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                    {rest.map((it, i) => (
                        <Reveal key={it.slug} delay={(i % 3) * 0.06}>
                            <Link to={`/insights/${it.slug}`} className="block group" data-testid={`insight-${it.slug}`}>
                                <div className="overflow-hidden rounded-2xl aspect-[4/3] bg-muted">
                                    <img src={it.cover_image} alt={it.cover_image_alt || ""} loading="lazy"
                                         className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105 grayscale-[20%] group-hover:grayscale-0" />
                                </div>
                                <p className="label mt-5">{it.category} · {it.read_minutes} min</p>
                                <h3 className="font-serif text-2xl mt-3 tracking-tight leading-tight group-hover:text-foreground">{it.title}</h3>
                                <p className="prose-body text-sm mt-3 line-clamp-3">{it.excerpt}</p>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </Section>
        </Layout>
    );
}
